
import { useState, useEffect, useRef, memo } from 'react';
import { usePlaylistDerivatives } from '@/hooks/usePlaylistDerivatives';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { AlertCircle, ArrowRight, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { UnifiedDerivativeDisplay } from '@/components/derivatives/UnifiedDerivativeDisplay';
import { useLanguage } from '@/contexts/LanguageContext';

interface SimpleDerivativesShowcaseProps {
  playlistId: string;
}

const SimpleDerivativesShowcase = ({ playlistId }: SimpleDerivativesShowcaseProps) => {
  const { t } = useLanguage();
  const { derivatives, isLoading, error, loadMore, hasMore } = usePlaylistDerivatives(playlistId);
  const [isVisible, setIsVisible] = useState(false);
  const fetchAttempted = useRef(false);
  const [visibleItemCount, setVisibleItemCount] = useState(6);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Initial number of creations to display (6 = 2 rows of 3)
  const initialItemsToShow = 6;

  useEffect(() => {
    // Use Intersection Observer to detect when the component enters viewport
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    
    // Get current reference to the container
    const sectionRef = document.getElementById('simple-derivatives-showcase');
    if (sectionRef) {
      observer.observe(sectionRef);
    }
    
    return () => {
      if (sectionRef) {
        observer.unobserve(sectionRef);
      }
    };
  }, []);

  useEffect(() => {
    if (!isLoading) {
      fetchAttempted.current = true;
    }
  }, [isLoading]);

  // Log the state of isLoading and derivatives count for debugging
  useEffect(() => {
    console.log('SimpleDerivativesShowcase - Loading status:', isLoading);
    console.log('SimpleDerivativesShowcase - Derivatives count:', derivatives?.length || 0);
    console.log('SimpleDerivativesShowcase - Playlist ID:', playlistId);
    if (error) {
      console.error('SimpleDerivativesShowcase - Error:', error);
    }
  }, [isLoading, derivatives, error, playlistId]);

  // Function to handle the "Voir plus" button click
  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    
    try {
      // Either use the hook's loadMore function or increase the visible count locally
      if (hasMore && loadMore) {
        await loadMore();
      }
      
      // Increase the visible item count regardless
      setVisibleItemCount(prev => {
        const newCount = prev + initialItemsToShow;
        console.log(`Increasing visible items from ${prev} to ${newCount}`);
        return newCount;
      });
    } catch (error) {
      console.error("Error loading more derivatives:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Handle error case more gracefully
  if (error) {
    return (
      <div className="py-8" id="simple-derivatives-showcase">
        <div className="container mx-auto px-4">
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error || "Une erreur s'est produite lors du chargement des créations"}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="py-16" id="simple-derivatives-showcase">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Créations de la communauté</h2>
          <p className="text-muted-foreground mb-8 text-center max-w-2xl mx-auto">
            Découvrez les meilleures créations réalisées par notre communauté de remixeurs à partir des extraits du film.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="aspect-[9/16] rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  // No derivatives to display - return minimal content instead of null
  if (!derivatives || derivatives.length === 0) {
    return (
      <div className="py-8" id="simple-derivatives-showcase">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Créations de la communauté</h2>
          <p className="text-muted-foreground mb-8 text-center max-w-2xl mx-auto">
            Les créations de la communauté seront bientôt disponibles.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-16" id="simple-derivatives-showcase">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Créations de la communauté</h2>
        <p className="text-muted-foreground mb-8 text-center max-w-2xl mx-auto">
          Découvrez les meilleures créations réalisées par notre communauté de remixeurs à partir des extraits du film.
        </p>
        
        <div className="mb-8">
          <UnifiedDerivativeDisplay
            items={derivatives}
            displayMode="grid"
            columns={{ sm: 2, md: 3, lg: 3, xl: 3 }} // 3 columns on larger screens
            initialItemsToShow={visibleItemCount} // Use our state to control visibility
            isLoading={isLoading}
            error={error}
            emptyMessage="Aucune création disponible pour le moment"
            loadingMessage="Chargement des créations..."
            forceVerticalLayout={true}
            showLoadMore={false} // Don't use the internal load more button
          />
          
          {/* Custom "See more" button - only show if we have more items than currently visible */}
          {derivatives.length > visibleItemCount && (
            <div className="flex justify-center mt-8">
              <Button 
                variant="default" 
                size="lg"
                className="min-w-[200px]"
                onClick={handleLoadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  <>
                    {t('common.viewMore')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimpleDerivativesShowcase;
